class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
    requestAnimationFrame(() => this.updateDisplayValue());
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const isPrefixed = Boolean(this.closest('.facet-short--prefixed'));

    let maxWidth = 0;
    Array.from(this.options).forEach((opt) => {
      const text = isPrefixed ? `Sort: ${opt.text}` : opt.text;
      const textElement = this.createTextElement(text, selectStyle);
      document.body.appendChild(textElement);
      maxWidth = Math.max(maxWidth, textElement.offsetWidth);
      textElement.remove();
    });

    this.style.setProperty('--width', `${maxWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateDisplayValue();
    this.updateFormParams(event);
  }

  updateDisplayValue() {
    const wrapper = this.closest('.facet-short--prefixed');
    const valueEl = wrapper?.querySelector('[data-facet-short-value]');
    if (!valueEl) return;
    const selectedText = this.options?.[this.selectedIndex]?.text || '';
    valueEl.innerHTML = selectedText ? `<strong>Sort:</strong>&nbsp;&nbsp;${selectedText}` : '<strong>Sort:</strong>';
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-short', FacetShort, { extends: 'select' });

// Standalone facet toggle (e.g. brand pills rendered outside the facet-form).
// Builds its add/remove URL from the current page URL + its own
// data-filter-param/data-filter-value at click time (not the server-rendered
// href), so it stays correct as other filters change, and toggles its own
// active class instead of relying on a server re-render — the pill set itself
// never changes based on other filters or their result counts.
class FacetPill extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onPillClick.bind(this));
  }

  onPillClick(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();
    if (!form) return;

    event.preventDefault();
    const param = this.dataset.filterParam;
    const value = this.dataset.filterValue;
    const url = new URL(window.location.href);

    if (param && value) {
      const currentValues = url.searchParams.getAll(param);
      const isActive = currentValues.includes(value);
      url.searchParams.delete(param);
      (isActive ? currentValues.filter((v) => v !== value) : [...currentValues, value]).forEach((v) =>
        url.searchParams.append(param, v)
      );
      this.classList.toggle('brand-filter-pill--active', !isActive);
      this.setAttribute('aria-pressed', String(!isActive));
    }

    url.searchParams.delete('page');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
    form.renderSection(url.toString(), event);
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}
customElements.define('facet-pill', FacetPill, { extends: 'a' });

(() => {
  function applyToolbarSort(value, event) {
    const form = document.querySelector('form[is="facet-form"]');
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    if (form && typeof form.renderSection === 'function') {
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    } else {
      window.location.assign(url.toString());
    }
  }

  function onChange(event) {
    const input = event.target;
    if (!input?.matches?.('input[type="radio"][data-sort-by-option]')) return;
    if (!input.closest('[data-toolbar-sort]')) return;

    applyToolbarSort(input.value, event);
  }

  function bindOnce() {
    if (document.body.dataset.toolbarSortRadiosBound) return;
    document.body.dataset.toolbarSortRadiosBound = 'true';
    document.addEventListener('change', onChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOnce);
  } else {
    bindOnce();
  }
})();

// Close the collection price dropdown when clicking outside / pressing Escape.
// The price dropdown is rendered as a plain <details> (not accordion-details), so we handle this globally.
(() => {
  let isBound = false;

  function closeAllPriceDropdowns(exceptEl) {
    document.querySelectorAll('details.facet-price-dropdown[open]').forEach((el) => {
      if (exceptEl && exceptEl === el) return;
      el.removeAttribute('open');
    });
  }

  function bindOnce() {
    if (isBound) return;
    isBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const clickedDropdown = target?.closest?.('details.facet-price-dropdown');
        if (clickedDropdown) {
          // Close other open price dropdowns when opening/using one.
          closeAllPriceDropdowns(clickedDropdown);
          return;
        }

        // Clicked outside any price dropdown.
        closeAllPriceDropdowns();
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAllPriceDropdowns();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOnce);
  } else {
    bindOnce();
  }

  // Re-bind after AJAX facet updates (safe due to guard).
  if (window.FoxTheme?.pubsub?.subscribe) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, bindOnce);
  }
})();

// Collection toolbar (mobile/tablet/laptop): keep horizontal scroll when opening filters. The scroll
// element must not toggle overflow:visible (resets scrollLeft). Panels use position:fixed below xl.
//
// iOS/WebKit note: position:fixed on a descendant of this sticky, horizontally-scrolling strip does not
// reliably escape the strip's overflow:hidden (a long-standing WebKit quirk affecting every iOS browser,
// since they all share the WebKit engine — this is not a Safari-only issue, every iOS browser hits it).
// Where supported, the panel is also promoted to the browser's "top layer" via the Popover API
// (https://caniuse.com/mdn-api_htmlelement_showpopover — Safari 17+ / iOS 17+), which is specifically
// designed to escape ancestor clipping/stacking without moving the element in the DOM. It's added and
// removed purely at the attribute/JS level, so nothing about markup, CSS, or event bubbling changes —
// same visual result, same class names, same styling as before; this only fixes what actually renders it.
(() => {
  const FIXED_ATTR = 'data-collection-toolbar-panel-fixed';
  const GAP = 6;
  const VIEWPORT_PAD = 12;
  const MAX_PANEL_W = 448;
  const supportsPopover = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

  function isNarrowToolbar() {
    return window.matchMedia('(max-width: 1279.98px)').matches;
  }

  function showPanelPopover(panel) {
    if (!supportsPopover) return;
    if (!panel.hasAttribute('popover')) panel.setAttribute('popover', 'manual');
    if (!panel.matches(':popover-open')) {
      try {
        panel.showPopover();
      } catch (e) {
        // Ignore: e.g. popover already open, or element not yet connected.
      }
    }
  }

  function hidePanelPopover(panel) {
    if (!panel.hasAttribute('popover')) return;
    if (supportsPopover && panel.matches(':popover-open')) {
      try {
        panel.hidePopover();
      } catch (e) {
        // Ignore: e.g. popover already hidden.
      }
    }
    // Fully remove (not just hide) so the panel reverts to plain native <details> visibility —
    // otherwise the browser's own [popover]:not(:popover-open) { display: none } would stick even
    // once we're back on a wide layout that no longer uses fixed positioning for this panel.
    panel.removeAttribute('popover');
  }

  function positionCollectionToolbarPanel(details) {
    const strip = details.closest('.collection__toolbar-swipe');
    if (!strip || !isNarrowToolbar()) return;

    const panel = details.querySelector(':scope > .facet-price-dropdown__panel');
    const summary = details.querySelector(':scope > summary');
    if (!panel || !summary) return;

    showPanelPopover(panel);

    const sr = summary.getBoundingClientRect();
    const vw = window.innerWidth;
    const maxW = Math.max(0, Math.min(MAX_PANEL_W, vw - 2 * VIEWPORT_PAD));
    let w = Math.min(Math.max(sr.width, 160), maxW);

    let left = sr.left;
    if (left + w > vw - VIEWPORT_PAD) left = vw - VIEWPORT_PAD - w;
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

    panel.setAttribute(FIXED_ATTR, '');
    panel.style.position = 'fixed';
    panel.style.left = `${left}px`;
    panel.style.top = `${sr.bottom + GAP}px`;
    panel.style.width = `${w}px`;
    panel.style.right = 'auto';
    panel.style.zIndex = '100';
    panel.style.maxHeight = 'min(60vh, 28rem)';
    panel.style.overflowY = 'auto';
    panel.style.boxSizing = 'border-box';
  }

  function clearCollectionToolbarPanel(details) {
    const panel = details.querySelector(':scope > .facet-price-dropdown__panel');
    if (!panel || !panel.hasAttribute(FIXED_ATTR)) return;
    panel.removeAttribute(FIXED_ATTR);
    panel.style.removeProperty('position');
    panel.style.removeProperty('left');
    panel.style.removeProperty('top');
    panel.style.removeProperty('width');
    panel.style.removeProperty('right');
    panel.style.removeProperty('z-index');
    panel.style.removeProperty('max-height');
    panel.style.removeProperty('overflow-y');
    panel.style.removeProperty('box-sizing');
    hidePanelPopover(panel);
  }

  const stripScrollHandlers = new WeakMap();
  let winListeners = 0;
  let repositionRaf = 0;

  function repositionOpenCollectionPanels() {
    if (!isNarrowToolbar()) {
      document.querySelectorAll('.collection__toolbar-swipe details.facet-price-dropdown[open]').forEach((d) => {
        clearCollectionToolbarPanel(d);
      });
      if (winListeners > 0) {
        winListeners = 0;
        window.removeEventListener('scroll', scheduleReposition, true);
        window.removeEventListener('resize', scheduleReposition);
      }
      document.querySelectorAll('.collection__toolbar-swipe').forEach((strip) => removeStripScrollListener(strip));
      return;
    }
    document.querySelectorAll('.collection__toolbar-swipe details.facet-price-dropdown[open]').forEach((d) => {
      positionCollectionToolbarPanel(d);
    });
  }

  function scheduleReposition() {
    cancelAnimationFrame(repositionRaf);
    repositionRaf = requestAnimationFrame(repositionOpenCollectionPanels);
  }

  function bindWindowReposition() {
    if (winListeners++ === 0) {
      window.addEventListener('scroll', scheduleReposition, true);
      window.addEventListener('resize', scheduleReposition);
    }
  }

  function unbindWindowReposition() {
    if (--winListeners <= 0) {
      winListeners = 0;
      window.removeEventListener('scroll', scheduleReposition, true);
      window.removeEventListener('resize', scheduleReposition);
    }
  }

  function ensureStripScrollListener(strip) {
    if (stripScrollHandlers.has(strip)) return;
    const handler = () => scheduleReposition();
    stripScrollHandlers.set(strip, handler);
    strip.addEventListener('scroll', handler, { passive: true });
  }

  function removeStripScrollListener(strip) {
    const handler = stripScrollHandlers.get(strip);
    if (!handler) return;
    strip.removeEventListener('scroll', handler);
    stripScrollHandlers.delete(strip);
  }

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.matches?.('details.facet-price-dropdown')) return;

      const strip = details.closest('.collection__toolbar-swipe');
      if (!strip) return;

      if (!details.open) {
        clearCollectionToolbarPanel(details);
        const stillOpen = strip.querySelector('details.facet-price-dropdown[open]');
        if (!stillOpen) {
          removeStripScrollListener(strip);
          unbindWindowReposition();
        } else if (isNarrowToolbar()) {
          requestAnimationFrame(() => positionCollectionToolbarPanel(stillOpen));
        }
        return;
      }

      if (!isNarrowToolbar()) return;

      requestAnimationFrame(() => {
        positionCollectionToolbarPanel(details);
        ensureStripScrollListener(strip);
        bindWindowReposition();
      });
    },
    true
  );
})();

// Search + clear-all within each list/boolean facet panel; clear price range from price panel.
(() => {
  function applyFacetOptionSearch(input) {
    const panel = input.closest('.parent-display');
    if (!panel) return;
    const q = input.value.trim().toLowerCase();
    const options = panel.querySelectorAll('.facet-filter-option');
    const showMore = panel.querySelector('show-more-button');

    if (!q) {
      options.forEach((li) => li.classList.remove('facet-search-hidden'));
      if (showMore) showMore.classList.remove('hidden');
      const labelShowLess = panel.querySelector('.label-show-less');
      const expanded = labelShowLess && !labelShowLess.classList.contains('hidden');
      panel.querySelectorAll('.show-more-item').forEach((li) => {
        if (expanded) li.classList.remove('hidden');
        else li.classList.add('hidden');
      });
      return;
    }

    if (showMore) showMore.classList.add('hidden');

    options.forEach((li) => {
      const label = (li.dataset.facetLabel || '').toLowerCase();
      const match = label.includes(q);
      li.classList.toggle('facet-search-hidden', !match);
      if (match) li.classList.remove('hidden');
    });
  }

  function clearFacetOptions(btn) {
    const root = btn.closest('[data-filter]');
    if (!root) return;
    const panel = btn.closest('.parent-display');
    const search = panel?.querySelector('.facet-filter-panel__search');
    if (search) {
      search.value = '';
      applyFacetOptionSearch(search);
    }

    let changed = false;
    root.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach((cb) => {
      if (cb.checked) {
        cb.checked = false;
        changed = true;
      }
    });

    const form = root.closest('form[is="facet-form"]');
    if (changed && form) {
      form.dirty = true;
      form.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  }

  function clearFacetPrice(btn) {
    const root = btn.closest('[data-filter]');
    const pr = root?.querySelector('price-range');
    const form = btn.closest('form[is="facet-form"]');
    if (!pr || !form) return;

    const inputs = pr.querySelectorAll('.facet-range__input');
    const minIn = inputs[0];
    const maxIn = inputs[1];
    const ranges = pr.querySelectorAll('input[type="range"]');
    const minR = ranges[0];
    const maxR = ranges[ranges.length - 1];
    const wrap = pr.querySelector('.facet-range-wrapper');

    if (minIn) minIn.value = '';
    if (maxIn) maxIn.value = '';
    if (minR) minR.value = minR.min;
    if (maxR) maxR.value = maxR.max;
    if (wrap) {
      wrap.style.setProperty('--range-min', '0%');
      wrap.style.setProperty('--range-max', '100%');
    }

    form.dirty = true;
    form.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  function onDocumentClick(event) {
    const btn = event.target.closest('.facet-filter-panel__clear');
    if (!btn) return;
    if (btn.dataset.facetClear === 'price') {
      event.preventDefault();
      clearFacetPrice(btn);
    } else if (btn.dataset.facetClear === 'options') {
      event.preventDefault();
      clearFacetOptions(btn);
    }
  }

  function onDocumentInput(event) {
    const input = event.target.closest('.facet-filter-panel__search');
    if (!input) return;
    applyFacetOptionSearch(input);
  }

  function bindOnce() {
    if (document.body.dataset.facetPanelToolsBound) return;
    document.body.dataset.facetPanelToolsBound = 'true';
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('input', onDocumentInput, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOnce);
  } else {
    bindOnce();
  }

  if (window.FoxTheme?.pubsub?.subscribe) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, bindOnce);
  }
})();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    this.minPriceInput.addEventListener('focus', this.minPriceInput.select);
    this.maxPriceInput.addEventListener('focus', this.maxPriceInput.select);
    this.minPriceInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxPriceInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.maxPriceInput.value || event.target.max) - 1),
      event.target.min
    );
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.minPriceInput.value || event.target.min) + 1),
      event.target.max
    );
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value),
      parseInt(this.maxPriceInput.value || event.target.max) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value),
      parseInt(this.minPriceInput.value || event.target.min) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

const FACET_FILTER_CONTAINER_SELECTOR =
  '#FacetFiltersContainer, #MobileFacetFiltersContainer, #DrawerFacetFiltersContainer';

function syncFacetActiveTriggerClasses(fromEl, toEl) {
  if (!fromEl || !toEl) return;
  ['facet-filter-dropdown--has-active', 'facet-accordion-details--has-active'].forEach((cls) => {
    toEl.classList.toggle(cls, fromEl.classList.contains(cls));
  });
}

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();
    /** data-index of [data-filter] that triggered the last change; skip replacing it on AJAX refresh (keeps panel search + DOM). */
    this.pendingFacetSkipIndex = null;
    /** Bumped per submission so a slow earlier response can't render over a newer one's results. */
    this.renderToken = 0;

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange(event) {
    // Only named controls reach FormData, so a change from anything else cannot alter the URL and
    // must not submit. The per-panel option search is the case that bites: it is unnamed (it only
    // filters the visible list) and fires `change` on blur — which is exactly the blur you cause by
    // clicking an option after typing a query. That submitted a request built a beat before the
    // checkbox flipped, i.e. an *unfiltered* one, racing the checkbox's own request; whichever
    // landed last won the grid, while the URL hash — written synchronously — kept the right value.
    // Hence: first pick after typing showed every product, and a refresh showed the right ones.
    if (!event.target?.name) return;

    this.dirty = true;
    const filterRoot = event.target?.closest?.('[data-filter]');
    this.pendingFacetSkipIndex = filterRoot?.dataset?.index ?? null;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const skipFilterIndex = this.pendingFacetSkipIndex;
    this.pendingFacetSkipIndex = null;

    const url = this.buildUrl().toString();
    this.renderSection(url, event, skipFilterIndex);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    this.appendActiveBrandFilterPills(url);

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  // Brand pills live outside this form, so FormData never picks them up —
  // without this, submitting any other filter (Model/Storage/Price) would
  // silently drop whatever brand is currently selected.
  appendActiveBrandFilterPills(url) {
    const container = document.getElementById('BrandFilterPills');
    if (!container) return;

    container.querySelectorAll('.brand-filter-pill--active[data-filter-param]').forEach((pill) => {
      const { filterParam, filterValue } = pill.dataset;
      if (!url.searchParams.getAll(filterParam).includes(filterValue)) {
        url.searchParams.append(filterParam, filterValue);
      }
    });
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 200,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event, skipFilterIndex = null) {
    // Every submission gets a token; only the newest one is allowed to paint. Two facet changes in
    // quick succession are two independent fetches, and nothing else guarantees they resolve in the
    // order they were sent.
    this.renderToken += 1;

    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url, event, skipFilterIndex)
      : this.renderSectionFromFetch(url, event, skipFilterIndex);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event, skipFilterIndex = null) {
    this.beforeRenderSection();
    const start = performance.now();
    const token = this.renderToken;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            // Superseded while in flight: still cache it, but let the newer render own the DOM —
            // including afterRenderSection, which clears the loading state once it lands.
            this.cachedMap.set(url, responseText);
            if (token !== this.renderToken) return;

            this.renderFilters(responseText, event, skipFilterIndex);
            this.renderFiltersActive(responseText);
            this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);
            this.syncBrandFilterPills(url);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event, skipFilterIndex = null) {
    this.beforeRenderSection();
    const token = this.renderToken;

    setTimeout(() => {
      if (token !== this.renderToken) return;

      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event, skipFilterIndex);
      this.renderFiltersActive(responseText);
      this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);
      this.syncBrandFilterPills(url);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(responseText, event, skipFilterIndex = null) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    const facetElementsFormDom = document.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter], #DrawerFacetFiltersContainer [data-filter]'
    );

    Array.from(facetElementsFormDom).forEach((currentElement) => {
      if (!Array.from(facetElements).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('hidden');
      }
    });

    const skipIdx = skipFilterIndex != null && skipFilterIndex !== '' ? String(skipFilterIndex) : null;
    const matchesIndex = (element) => skipIdx != null && element.dataset.index === skipIdx;
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetElements.forEach((element) => {
      const container = element.closest(FACET_FILTER_CONTAINER_SELECTOR);
      if (!container?.id) return;
      const host = document.getElementById(container.id);
      if (!host) return;
      const idx = element.getAttribute('data-index');
      if (idx == null) return;
      const filter = host.querySelector(`[data-filter][data-index="${CSS.escape(idx)}"]`);
      if (filter) syncFacetActiveTriggerClasses(element, filter);
    });

    facetsToRender.forEach((element) => {
      const container = element.closest(FACET_FILTER_CONTAINER_SELECTOR);
      if (!container?.id) return;
      const host = document.getElementById(container.id);
      if (!host) return;
      const idx = element.getAttribute('data-index');
      if (idx == null) return;
      const filter = host.querySelector(`[data-filter][data-index="${CSS.escape(idx)}"]`);
      if (filter !== null) {
        filter.classList.remove('hidden');
        if (filter.tagName === 'DETAILS') {
          // Panel may be temporarily detached to <body> (open on a narrow toolbar, escaping clipping) —
          // in that case skip the sync rather than throw; it'll pick up fresh content next time it opens.
          const targetPanel = filter.querySelector('summary + *');
          const sourcePanel = element.querySelector('summary + *');
          if (targetPanel && sourcePanel) targetPanel.innerHTML = sourcePanel.innerHTML;
        } else {
          const prevDetails = filter.querySelector('details.facet-price-dropdown');
          const wasOpen = prevDetails?.hasAttribute('open') ?? false;
          filter.innerHTML = element.innerHTML;
          if (wasOpen) {
            const nextDetails = filter.querySelector('details.facet-price-dropdown');
            if (nextDetails) nextDetails.setAttribute('open', '');
          }
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    const next = parsedHTML.getElementById(id);
    document.getElementById(id).innerHTML = next ? next.innerHTML : '';
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;

    const layoutSwitcher = document.querySelector('layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const sync = (domId) => {
      const live = document.getElementById(domId);
      const next = parsedHTML.getElementById(domId);
      if (live && next) live.innerHTML = next.innerHTML;
    };
    sync('SortByContainer');
    sync('SortByContainerCollectionMobile');
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  // Toggles active state on the existing brand pills based on the URL just
  // submitted — never replaces the pill set itself, so the options stay the
  // same regardless of which other filters (or their result counts) changed.
  syncBrandFilterPills(url) {
    const container = document.getElementById('BrandFilterPills');
    if (!container) return;

    const nextUrl = new URL(url, window.location.href);
    container.querySelectorAll('[data-filter-param]').forEach((pill) => {
      const isActive = nextUrl.searchParams.getAll(pill.dataset.filterParam).includes(pill.dataset.filterValue);
      pill.classList.toggle('brand-filter-pill--active', isActive);
      pill.setAttribute('aria-pressed', String(isActive));
    });
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetToggler extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.facetEl = document.getElementById('FacetFiltersContainer');
    this.addEventListener('click', this.onClickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick(evt) {
    evt.preventDefault();

    if (this.facetEl.classList.contains('xl:block')) {
      this.facetEl.classList.remove('xl:block');
    } else {
      this.facetEl.classList.add('xl:block');
    }
  }
}
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.f-column').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}
customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'hypertheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  /**
   * @param {event|element} evt
   */
  onButtonClick(evt) {
    const target = evt.currentTarget ? evt.currentTarget : evt;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);
        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;

    productGrid.dataset.layout = layoutMode;

    const { gridClass, listClass } = productGrid.dataset;
    if ((gridClass || listClass) && gridClass !== listClass) {
      const productCards = productGrid.querySelectorAll('.product-card');
      productCards &&
        productCards.forEach((card) => {
          if ('grid' === layoutMode) {
            listClass && card.classList.remove(listClass);
            gridClass && card.classList.add(gridClass);
          } else {
            gridClass && card.classList.remove(gridClass);
            listClass && card.classList.add(listClass);
          }
        });
    }

    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });
    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);
